import t from "@/lang/lang";
import { Label } from "@/shared/components/ui/label";
import { FloatNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import { useState } from "react";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { useQpmOfferSetCertifiedTrainingCoursePriceMutation } from "@/services/backend/qpm/offers/api/offer-api";
import { Offer } from "@/services/backend/qpm/offers/types/offer";
import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import {
  fieldErrorsForPrefix,
  resetField,
} from "@/shared/lib/errors/validation-error";

type OfferCertifiedTrainingCoursePriceEditorProps = {
  offer: Offer;
};

export function OfferCertifiedTrainingCoursePriceEditor({
  offer,
}: OfferCertifiedTrainingCoursePriceEditorProps) {
  const { validationError, setValidationError } = useOfferContext();
  const fieldErrors = fieldErrorsForPrefix(validationError, "");

  const [request, setRequest] = useState({
    offerId: offer.id,
    certifiedTrainingCoursePrice: offer.certifiedTrainingCoursePrice,
  });

  const [
    setCertifiedTrainingCoursePrice,
    { error, isLoading, isSuccess, reset },
  ] = useQpmOfferSetCertifiedTrainingCoursePriceMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    setCertifiedTrainingCoursePrice,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
    { toastError: true },
  );

  return (
    <div className="grid content-start gap-1.5 lg:max-w-md">
      <Label className="min-h-5 text-sm">{t("Schulung Sachkunde")}</Label>
      <div className="flex items-center gap-2">
        <FloatNumberInput
          className="w-full max-w-40"
          value={request.certifiedTrainingCoursePrice}
          disabled={offer.immutable}
          onChange={(next) => {
            setValidationError(
              resetField(validationError, "certifiedTrainingCoursePrice"),
            );
            setRequest((current) => {
              if (current.certifiedTrainingCoursePrice === next) {
                return current;
              }

              return {
                ...current,
                certifiedTrainingCoursePrice: Math.max(0, next),
              };
            });
          }}
        />
        <span className="text-muted-foreground text-sm">€</span>
      </div>
      <div className="text-destructive min-h-5 text-sm">
        {(fieldErrors?.certifiedTrainingCoursePrice ?? [])[0] ?? ""}
      </div>
    </div>
  );
}
